import { formatPriceCents } from 'sidecar-utilities'

import { getCareCosts } from 'utils/care'
import { getCostListType } from 'pages/CareV2/utils'

/**
 * Calculates various values related to care coverage and benefits based on care
 * details and policy information
 *
 * @param {Object} careDetail - The care detail object containing care-specific
 *   information
 * @param {boolean} [careDetail.isCoverAtCost] - Indicates if the care is
 *   covered at cost
 * @param {Object} policy - The policy object containing insurance information
 * @param {string} [policy.insuranceProduct] - The type of insurance product
 * @returns {Object} An object containing calculated values:
 * @returns {boolean} .careCovered - Indicates if the care is covered
 * @returns {boolean} .isCoverAtCostNotEB - Indicates if care is covered at cost
 *   and not a fixed indemnity product
 * @returns {number | null} .shouldCost - The calculated cost of care
 * @returns {number | null} .benefitAmount - The benefit amount
 * @returns {string | number | null} .benefitAmountOrCovered - Either 'Covered
 *   at cost' or the shouldCost value
 * @returns {boolean} .hasPrice - Indicates if there is a price (either
 *   shouldCost or benefitAmount)
 */
export const parseDetailWithPolicy = (initialState, careDetail, policy) => {
  // Early return if we don't have the data we need
  if (!careDetail || !policy) {
    return initialState
  }

  // We need to know what product we're dealing with to be able to
  // determine the costs of a care item.
  const insuranceProduct = policy?.insuranceProduct || ''
  const isEB = insuranceProduct === 'FIXED_INDEMNITY'

  // costListType determines which kind of list we show for price comparison.
  // Determines either a list of providers or a list of pharamcies and whether
  // we show the view more button too.
  const costListType = getCostListType(
    careDetail?.filters,
    careDetail?.categorySlug,
  )

  // We need to get the receiveAt value from the filters because it
  // makes a difference in how much something costs.
  const receiveAt = careDetail?.filters
    ?.find(filter => filter.name === 'receiveAt')
    ?.options?.find(option => option.selected)?.value

  // The cost and benefit amount of this care item depend on several factors
  // including what kind of service, where it's performed, and what we cover.
  const { shouldCost, benefitAmount } = getCareCosts(receiveAt, careDetail)

  // Some care items can be covered at cost so grab the flag from detail.
  // Use caution with this messaging, err on the side of not covered at cost.
  const { isCoverAtCost = false } = careDetail || {}

  // FI/EB plans do not have anything covered at cost so this
  // is important to be able to accurately determine pricing.
  const isCoverAtCostNotEB = isCoverAtCost && !isEB

  // We consider that an item is covered if we have a benefit amount for it
  // and we're covering it at cost (excludes EB plans).
  const careCovered = !!benefitAmount || isCoverAtCostNotEB

  // We'll need to show either the benefit amount or that the item is
  // covered at cost.
  const benefitAmountOrCovered = isCoverAtCostNotEB
    ? 'Covered at cost'
    : formatPriceCents(benefitAmount)

  return {
    careCovered,
    isCoverAtCostNotEB,
    shouldCost,
    benefitAmount,
    benefitAmountOrCovered,
    costListType,
  }
}
